import { Box, Typography } from '@mui/material';
import {
  formatWindZoneLabel,
  TravelerTruss,
  TrussSpecifications as TrussSpecsData,
} from '../types';

interface TrussSpecificationsProps {
  truss: TravelerTruss;
  specs: TrussSpecsData | null;
  isLocked: boolean;
}

interface SpecFieldProps {
  label: string;
  value: string | null | undefined;
  /** When true, row renders with em dash placeholder if value is missing (UDOMI-938). */
  forceDisplay?: boolean;
}

export const formatSpecDisplayValue = (value: string | null | undefined): string => {
  if (value == null || value.trim() === '') return '—';
  return value;
};

/**
 * BR-9 Cord Angle from live roof pitch so traveler updates when pitch changes (UDOMI-938).
 * Formula: CordAngle = 90 - DEGREES(ATAN(Pitch / 12))
 */
export const formatCordAngleFromRoofPitch = (
  roofPitch: string | null | undefined
): string | null => {
  if (!roofPitch) return null;
  const colonIdx = roofPitch.indexOf(':');
  const raw = colonIdx > 0 ? roofPitch.slice(0, colonIdx) : roofPitch;
  const pitch = Number.parseFloat(raw);
  if (!Number.isFinite(pitch) || pitch < 0.5 || pitch > 12) return null;
  const degrees = 90 - (Math.atan(pitch / 12) * 180) / Math.PI;
  return `${degrees.toFixed(1)}°`;
};

const SpecField = ({ label, value, forceDisplay = false }: SpecFieldProps) => {
  const display = formatSpecDisplayValue(value);
  if (!forceDisplay && display === '—') return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontSize: '11px', color: '#888', mb: 0.25 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: '#333',
          py: 1,
          borderBottom: '1px solid #eee',
        }}
      >
        {display}
      </Typography>
    </Box>
  );
};

const TrussSpecifications = ({ truss, specs, isLocked }: TrussSpecificationsProps) => {
  const isGable = truss.productType === 'Gable';
  const isLeanTo = truss.productType === 'LeanTo';
  const isHeader = truss.productType === 'Header';
  const cordAngleDisplay =
    formatCordAngleFromRoofPitch(truss.roofPitch) ?? specs?.cordAngle ?? null;
  const isMonoslope = truss.monoslopeTruss;

  return (
    <Box
      sx={{
        bgcolor: isLocked ? '#f0fff4' : '#f9f9f9',
        borderRadius: '6px',
        p: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#666',
          textTransform: 'uppercase',
          mb: 1.5,
        }}
      >
        Truss Specifications
      </Typography>

      <Box
        className="truss-specifications-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
        }}
      >
        <SpecField label="Wind Speed (MPH)" value={String(truss.windSpeed)} />
        <SpecField label="Wind Zone" value={formatWindZoneLabel(truss.windSpeed)} />
        <SpecField label="Snow Load (PSF)" value={String(truss.snowLoad)} />
        {specs && (
          <>
            <SpecField label="Truss Depth" value={specs.trussDepth} />
            {!isHeader && !isMonoslope && <SpecField label="Overhang" value={specs.overhang} />}
            {isMonoslope && (
              <>
                <SpecField label="Lower Overhang" value={specs.lowerOverhang} />
                <SpecField label="Upper Overhang" value={specs.upperOverhang} />
              </>
            )}
            <SpecField label="Post" value={specs.post} />
            {!isHeader && (
              <SpecField
                label="Cord Mat'l"
                value={specs.cordMaterial}
                forceDisplay={isGable || isLeanTo}
              />
            )}
            {!isHeader && (
              <SpecField label="Cord Angle" value={cordAngleDisplay} forceDisplay={isLeanTo} />
            )}
            {!isHeader && !isGable && <SpecField label="Slope" value={truss.roofPitch} />}
            {!isHeader && <SpecField label="Top Cord" value={specs.topCord} />}
            {!isHeader && <SpecField label="Bottom Cord" value={specs.bottomCord} />}
            {isGable && <SpecField label="Drill Hole Dim" value={specs.drillHoleDim} />}
            <SpecField label="Top Plates" value={specs.topPlates} />
            {!isHeader && (
              <SpecField
                label="Top Plate Height"
                value={specs.topPlateHeight}
                forceDisplay={isGable || isLeanTo}
              />
            )}
            <SpecField label="End Plates" value={specs.endPlates} />
            {isGable && <SpecField label="Angle Length" value={specs.angleLength} />}
            {isGable && <SpecField label="Cross Tie Length" value={specs.crossTieLength} />}
          </>
        )}
      </Box>

      {!specs && (
        <Typography sx={{ fontSize: '12px', color: '#888', mt: 1 }}>
          Calculated specifications are loading or unavailable.
        </Typography>
      )}

      {/* Carriage Bolts */}
      {truss.carriageBolts && specs?.carriageBoltsRequired && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#666',
              mb: 1,
            }}
          >
            Carriage Bolts
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
            }}
          >
            <SpecField label="Kits Required" value={specs.carriageBoltsRequired ? 'Yes' : 'No'} />
            <SpecField label="Size" value={specs.carriageBoltSize} />
            <SpecField label="No. of Kits" value={specs.carriageBoltKits?.toString()} />
          </Box>
        </Box>
      )}

      {/* Comments */}
      {truss.notes && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography
            sx={{
              fontSize: '11px',
              color: '#888',
              textTransform: 'uppercase',
              mb: 0.75,
            }}
          >
            Comments
          </Typography>
          <Box
            sx={{
              bgcolor: '#fffde7',
              p: '10px 12px',
              borderRadius: '4px',
              borderLeft: '3px solid #f1c40f',
              fontSize: '13px',
              color: '#5d4037',
            }}
          >
            {truss.notes}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TrussSpecifications;
